import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { VEmailInput } from '@client/components/_shared/forms/view-form/VEmailInput';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { VSwitch } from '@client/components/_shared/forms/view-form/VSwitch';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { UserFilters } from '@client/components/users/UserFilters';
import { useQueryErrorEffect } from '@client/hooks/api/useQueryErrorEffect';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { createFormStore } from '@client/stores/FormStore';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { isRetryingQueries, retryFailedQueries } from '@client/trpc/query-retry';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { isValidEmailAddress } from '@shared/utils/core/email-address';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Badge, Col, Divider, Row, Skeleton, Switch, Tooltip, Typography } from 'antd';
import clsx from 'clsx';
import { useState } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/handling-parties/details/$id')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Handling party details',
        },
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <HandlingPartyEdit />
        </PageController>
    );
}

const handlingPartyFormStore = createFormStore<RouterOutputs['handlingParty']['getHandlingParty']>();

type QueryParams = RouterInputs['user']['listUsers'];

type UserRow = NonNullable<RouterOutputs['user']['listUsers']>['rows'][number];

type UserOrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<UserRow, UserOrderBy>();

function HandlingPartyEdit() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { id } = Route.useParams();
    const { mutate } = trpc.handlingParty.updateHandlingParty.useMutation();
    const navigate = useNavigate();

    const [queryParams, setQueryParams] = useState<QueryParams>({
        limit: DEFAULT_PAGE_SIZE,
        orderBy: [{ firstName: 'asc' }],
    });
    const [shouldShowDisabled, setShouldShowDisabled] = useState(false);

    const listUsersQuery = trpc.user.listUsers.useQuery({
        ...queryParams,
        where: {
            ...queryParams.where,
            ...(!shouldShowDisabled && ({ disabled: false } satisfies QueryParams['where'])),
            HandlingParties: {
                some: {
                    handlingPartyId: id,
                },
            },
        },
    });

    const handlingPartyQuery = trpc.handlingParty.getHandlingParty.useQuery(id ? { id } : skipToken);
    useQueryErrorEffect(handlingPartyQuery.error, (error) => {
        match(error.data?.errorId)
            .with('recordNotFound', () => {
                void errorMessage
                    .showAsync(
                        "The handling party you are trying to access either doesn't exist or you don't have permissions to access it.",
                    )
                    .then(() =>
                        navigate({
                            to: '/console/$orgId/configuration/org-settings/handling-parties',
                            params: { orgId },
                        }),
                    );
            })
            .otherwise(() => {
                errorMessage.show(error);
            });
    });

    const { formData: handlingParty } = handlingPartyFormStore.useInit({
        entity: 'handlingParty',
        data: handlingPartyQuery.data,
        isLoading: handlingPartyQuery.isLoading,

        async onReload() {
            await handlingPartyQuery.refetch();
        },

        onSave(currentValue, newValue) {
            return new Promise((resolve) => {
                mutate(
                    { id: currentValue.id, set: newValue },
                    {
                        onSuccess() {
                            void trpcUtils.handlingParty.invalidate();
                            resolve({ success: true });
                        },
                        onError(error) {
                            if (error.data?.errorId === 'handlingParty.alreadyExists') {
                                message.error('This name is already in use.');
                                resolve({ success: true });
                                return;
                            }

                            resolve({ success: false, error });
                        },
                    },
                );
            });
        },
    });

    const tableProps = usePrismaTable<UserRow, QueryParams['where'], UserOrderBy>({
        data: listUsersQuery.data?.rows,
        total: listUsersQuery.data?.total,
        loading: listUsersQuery.isLoading,
        columns: [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'ID',
                sortable: 'id',
                cell: ({ value }) => value,
            }),
            columnHelper.accessor('firstName', {
                id: 'firstName',
                header: 'First name',
                sortable: 'firstName',
                cell: ({ value }) => value,
            }),
            columnHelper.accessor('lastName', {
                id: 'lastName',
                header: 'Last name',
                sortable: 'lastName',
                cell: ({ value }) => value,
            }),
            columnHelper.accessor('position', {
                id: 'position',
                header: 'Position',
                sortable: 'position',
                cell: ({ value }) => value || '-',
            }),
            columnHelper.accessor((row) => row.User?.email || row.email, {
                id: 'email',
                header: 'Email',
                sortable: 'User.email',
                cell: ({ value }) => (
                    <Typography.Link href={`mailto:${value}`} copyable={{ text: value }} className="text-sm">
                        {value}
                    </Typography.Link>
                ),
            }),
            columnHelper.accessor('disabled', {
                id: 'status',
                header: 'Status',
                sortable: 'disabled',
                cell: ({ row }) => (
                    <Badge
                        status={row.disabled ? 'error' : 'success'}
                        text={
                            <span className={clsx('font-medium', row.disabled ? 'text-red-600' : 'text-green-600')}>
                                {row.disabled ? 'Disabled' : 'Active'}
                            </span>
                        }
                    />
                ),
            }),
        ],
        rowKey: 'userId',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'handling-party-members-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => listUsersQuery.refetch(),
        },
        emptyState: {
            noRecordsTitle: shouldShowDisabled
                ? 'No members in this handling party'
                : 'No active members in this handling party',
        },
    });

    return (
        <OverlayPage
            title="Handling party details"
            size="large"
            afterClose={() => {
                void trpcUtils.handlingParty.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <Button type="secondary" onClick={onClose}>
                    Cancel
                </Button>
            )}
        >
            {(listUsersQuery.error ?? handlingPartyQuery.error) ? (
                <InlineQueryError
                    error={listUsersQuery.error ?? handlingPartyQuery.error}
                    onRetry={() => retryFailedQueries([listUsersQuery, handlingPartyQuery])}
                    retrying={isRetryingQueries([listUsersQuery, handlingPartyQuery])}
                />
            ) : (
                <>
                    <Outlet />

                    {id === handlingParty?.id ? (
                        <Row gutter={[16, 16]}>
                            <Col span={6}>
                                <VTextInput label="Name" field="name" formStore={handlingPartyFormStore} />
                            </Col>
                            <Col span={6}>
                                <VReadOnly label="Members" field="membersCount" formStore={handlingPartyFormStore} />
                            </Col>
                            <Col span={6}>
                                <VSwitch
                                    label="Disable handling party"
                                    field="isDisabled"
                                    formStore={handlingPartyFormStore}
                                />
                            </Col>
                            <Col span={6}>
                                <VEmailInput
                                    label="Notification email"
                                    field="notificationEmail"
                                    formStore={handlingPartyFormStore}
                                    maxLength={320}
                                    isValidEmail={isValidEmailAddress}
                                    helpMessage="Receives internal notifications for lodged claims. Leave blank to skip this handling party."
                                />
                            </Col>
                        </Row>
                    ) : (
                        <Skeleton active />
                    )}

                    <Divider />

                    <TableToolbar
                        filters={
                            <UserFilters
                                urlKey="handling-party-members-filters"
                                fieldsToHide={{ handlingPartyIds: true }}
                                onChange={(where) => setQueryParams((prev) => ({ ...prev, where, offset: 0 }))}
                            />
                        }
                        variant="enhanced"
                        actions={
                            <>
                                <Tooltip
                                    title={shouldShowDisabled ? 'Showing all members' : 'Showing only active members'}
                                >
                                    <Switch
                                        checked={shouldShowDisabled}
                                        onChange={setShouldShowDisabled}
                                        checkedChildren="All"
                                        unCheckedChildren="Active"
                                    />
                                </Tooltip>
                                <Button.Link
                                    to="/console/$orgId/configuration/org-settings/handling-parties/details/$id/users"
                                    params={{ orgId, id }}
                                    variant="primary"
                                >
                                    Manage members
                                </Button.Link>
                            </>
                        }
                    />

                    <PrismaTable {...tableProps} rowClassName={(row) => clsx(row.disabled && 'opacity-60')} />
                </>
            )}
        </OverlayPage>
    );
}
